import {ContentContainer} from '@coolcolduk/ui-mui';
import {groupArrayByKey} from '@coolcolduk/util';
import {Paper, Stack, Typography} from '@mui/material';
import {useMemo} from 'react';
import {useNavigate, useParams} from 'react-router';
import {ModuleTopic, QuizTimedMode, StandardSubject} from '@cp/model';
import ErrorComponent from '../../components/ErrorComponent/ErrorComponent';
import {env} from '../../config/env';
import {PrivateRouteProps} from '../../config/routeType';
import {useCreateAndNavigateToQuizSession} from '../../hooks/quizSession/useCreateAndNavigateToQuizSession';
import useGetExamModule from '../../hooks/examModules/useGetExamModule';
import useGetUserProgress from '../../hooks/userProgress/useGetUserProgress';
import LoadingPage from '../other/LoadingPage';
import CourseCategory from './CourseCategory';
import CourseHeader from './CourseHeader';

export default function CoursePage(_props: PrivateRouteProps) {
  const {examModuleId} = useParams<{examModuleId: string}>();
  const navigate = useNavigate();
  const {examModule, loading, error} = useGetExamModule(examModuleId ?? '', {skip: !examModuleId});
  const {progress, loading: progressLoading} = useGetUserProgress({
    examModuleId: examModuleId || '',
    skip: !examModuleId,
  });

  const {createAndNavigate, loading: creatingSession} = useCreateAndNavigateToQuizSession();

  // Group topics by category
  const topicsByCategory = useMemo((): Record<string, ModuleTopic[]> => {
    if (!examModule?.topics) return {};

    const topicsWithCategory: ModuleTopic[] = examModule.topics.map((topic: ModuleTopic) => ({
      ...topic,
      category: topic.category || env.topic.other,
    }));

    return groupArrayByKey(topicsWithCategory, 'category');
  }, [examModule?.topics]);

  const handleStartSession = async () => {
    if (!examModule || !examModule.topics || examModule.topics.length === 0) return;

    try {
      // Create a session with all topics (topic is undefined, so all topics will be used)
      await createAndNavigate(examModule, undefined, {
        // difficulty: 1, // default difficulty
        count: env.practice.defaultQuestions,
        timedMode: QuizTimedMode.PRACTICE,
        navigationType: 'practice',
      });
    } catch (err) {
      console.error('Failed to create practice session:', err);
    }
  };

  const handleCreatePracticeSession = async (topic: string, difficulty: number) => {
    if (!examModule) return;

    try {
      // Create a session with the topic and navigate to practice session
      await createAndNavigate(examModule, topic, {
        difficulty,
        count: env.practice.defaultQuestions,
        timedMode: QuizTimedMode.PRACTICE,
        navigationType: 'practice',
      });
    } catch (err) {
      console.error('Failed to create practice session:', err);
      throw err;
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (loading || progressLoading) {
    return <LoadingPage center minusAppBar />;
  }

  if (error || !examModule) {
    return <ErrorComponent message="Module not found" buttonText="Go back" handleClick={handleBack} />;
  }

  const subjectColor =
    examModule?.subject && examModule.subject in env.subjects
      ? env.subjects[examModule.subject as StandardSubject]
      : env.subjects.MATHEMATICS;

  return (
    <ContentContainer data-testid="course-page">
      <CourseHeader
        examModule={examModule}
        topicsCount={examModule.topics?.length || 0}
        categoriesCount={Object.keys(topicsByCategory).length}
        onBack={handleBack}
        onStartSession={handleStartSession}
        isCreatingSession={creatingSession}
      />

      {/* Topics Grouped by Category */}
      {Object.keys(topicsByCategory).length > 0 ? (
        <Stack spacing={3} data-testid="course-page-categories">
          {Object.entries(topicsByCategory).map(([category, topics]) => (
            <CourseCategory
              key={category}
              category={category}
              topics={topics}
              subjectColor={subjectColor}
              relatedScores={progress?.relatedScores || {}}
              progress={progress || undefined}
              onCreatePracticeSession={handleCreatePracticeSession}
              examModule={examModule}
            />
          ))}
        </Stack>
      ) : (
        <Paper sx={{p: 4, textAlign: 'center'}} data-testid="course-page-no-topics">
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No topics available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for new content
          </Typography>
        </Paper>
      )}
    </ContentContainer>
  );
}
